import styled, { css } from 'styled-components'
import tinycolor from 'tinycolor2'
import type { ComponentsTheme } from '../../providers/ComponentsThemeProvider'
import { getAutomatedStyleProp } from '../../providers/ComponentsThemeProvider'
import type {
  ActionIconContainerProps,
  AppendProps,
  BoxProps,
  IconContainerProps,
  InputProps,
  LabelProps,
  PrependProps,
  RootProps,
  SuggestionProps,
} from './types'

export const INPUT_PADDING = 15
export const PREPEND_MARGIN = 8
export const APPEND_MARGIN = 4
export const ICON_SIZE = 16
export const ACTION_ICON_SIZE = 12
export const ICON_MARGIN = 10

export const Root = styled.div<RootProps>`
  position: relative;

  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`

export const Container = styled.div`
  position: relative;
  width: 100%;
`

const getBoxBorderColor = ({
  $invalid,
  $focused,
  $transparent,
  theme,
}: BoxProps & { theme: ComponentsTheme }) => {
  if ($transparent) {
    return 'transparent'
  }

  if ($invalid && theme.mode === 'opacity') {
    return `${theme.automated?.styles?.inputError} !important`
  }

  if ($invalid) {
    return `var(--input-border-critical) !important`
  }

  if ($focused && theme.automated?.styles?.paleTextColor) {
    return `${theme.automated?.styles?.paleTextColor} !important`
  }

  if ($focused) {
    return `var(--input-border-active) !important`
  }

  if (theme.automated?.styles?.inputBorder) {
    return theme.automated?.styles?.inputBorder
  }

  return 'var(--input-border)'
}

const getBoxHoverBorderColor = ({ theme }: { theme: ComponentsTheme }) => {
  if (theme.automated?.styles?.paleTextColor && theme.mode === 'opacity') {
    return theme.automated?.styles?.paleTextColor
  }

  return 'var(--input-border-hover)'
}

export const getBoxBackground = ({
  $transparent,
  theme,
}: BoxProps & { theme: ComponentsTheme }) => {
  if ($transparent) {
    return 'transparent'
  }

  if (theme.automated?.styles?.colorBackground && theme.mode === 'opacity') {
    return theme.automated?.styles?.colorBackground
  }

  if (theme.automated?.styles?.inputBackground) {
    return theme.automated?.styles.inputBackground
  }

  return 'var(--input-bg)'
}

const getBoxDisabledBackground = ({
  $transparent,
  theme,
}: BoxProps & { theme: ComponentsTheme }) => {
  if ($transparent) {
    return 'transparent'
  }
  if (theme.automated?.styles?.inputBackground && theme.mode === 'opacity') {
    return tinycolor(theme.automated?.styles.colorOnBackground)
      .setAlpha(theme.automated?.styles.paleBackgroundAlpha)
      .toString()
  }

  return 'var(--bg-secondary)'
}

const getDisabledOpacity = ({ theme }: { theme: ComponentsTheme }) => {
  if (theme.mode === 'opacity') {
    return 0.3
  }

  return 1
}

export const Box = styled.div<BoxProps>`
  /* Input */
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  background: ${getBoxBackground};
  border: 1px solid ${getBoxBorderColor};
  display: flex;
  align-items: center;

  border-radius: ${getAutomatedStyleProp('inputCornerRadius', 'var(--radius-brand)')};

  ${({ $size, $fullHeight }) =>
    $size === 'large' &&
    !$fullHeight &&
    css`
      height: 56px;
    `}

  ${({ $size, $fullHeight }) =>
    $size === 'medium' &&
    !$fullHeight &&
    css`
      height: 48px;
    `}

  ${({ $size, $fullHeight }) =>
    $size === 'small' &&
    !$fullHeight &&
    css`
      height: 40px;
    `}

  ${({ $disabled, $hovered }) =>
    !$disabled &&
    css`
      &:hover {
        border-color: ${getBoxHoverBorderColor};
      }

      ${$hovered &&
      css`
        border-color: ${getBoxHoverBorderColor};
      `}
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: ${getDisabledOpacity};
      background: ${getBoxDisabledBackground};
      cursor: not-allowed;
    `}
`

export const Label = styled.label<LabelProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  color: ${getAutomatedStyleProp('paleTextColor', 'var(--text-secondary)')};
  pointer-events: none;
  font-size: var(--font-size-400);
  padding-left: var(--space-400);
  padding-right: var(--space-400);
  white-space: nowrap;

  user-select: none;

  ${({ $size, $hasValue }) =>
    $size === 'large' &&
    css`
      height: 56px;
      line-height: 56px;

      ${$hasValue &&
      css`
        font-size: var(--font-size-200);
        line-height: 34px;
      `}
    `}

  ${({ $size, $hasValue }) =>
    $size === 'medium' &&
    css`
      height: 48px;
      line-height: 48px;

      ${$hasValue &&
      css`
        font-size: var(--font-size-100);
        line-height: var(--font-line-height-100);
        letter-spacing: var(--font-letter-spacing-loosest);
      `}
    `}

  ${({ $size, $hasValue }) =>
    $size === 'small' &&
    css`
      height: 40px;
      line-height: 40px;

      ${$hasValue &&
      css`
        display: none;
      `}
    `}
`

const getTextColor = () => getAutomatedStyleProp('colorOnBackground', 'var(--text-primary)')

export const Input = styled.input<InputProps>`
  width: 100%;
  padding: 0 var(--space-350);
  background: transparent;
  border: 0;
  outline: 0;
  vertical-align: top;

  font-size: var(--font-size-400);
  color: ${getTextColor};

  ::placeholder {
    color: ${getAutomatedStyleProp('paleTextColor', 'var(--text-secondary)')};
  }

  &:disabled {
    color: ${getTextColor};
    cursor: not-allowed;
  }

  // https://css-tricks.com/snippets/css/change-autocomplete-styles-webkit-browsers/
  &:-webkit-autofill,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:hover {
    -webkit-text-fill-color: ${getTextColor};
    -webkit-background-clip: text;
    caret-color: ${getTextColor};
  }

  ${({ $centered }) =>
    $centered &&
    css`
      text-align: center;
    `}

  ${({ $inputSize, $hasLabel }) =>
    $inputSize === 'large' &&
    css`
      line-height: 54px;

      &:-webkit-autofill {
        & ~ ${Label} {
          font-size: var(--font-size-200);
          line-height: 34px;
        }
      }

      ${$hasLabel &&
      css`
        padding-top: var(--space-400);
        line-height: 38px;
      `}
    `}

  ${({ $inputSize, $hasLabel }) =>
    $inputSize === 'medium' &&
    css`
      line-height: 46px;

      &:-webkit-autofill {
        & ~ ${Label} {
          font-size: var(--font-size-100);
          line-height: var(--font-line-height-100);
          letter-spacing: var(--font-letter-spacing-loosest);
        }
      }

      ${$hasLabel &&
      css`
        padding-top: var(--space-350);

        line-height: var(--font-line-height-400); /* 150% */
        letter-spacing: var(--font-letter-spacing-normal);
      `}
    `}

  ${({ $inputSize, $hasLabel }) =>
    $inputSize === 'small' &&
    css`
      line-height: 38px;

      &:-webkit-autofill {
        & ~ ${Label} {
          font-size: var(--font-size-200);
          line-height: 34px;
        }
      }

      ${$hasLabel &&
      css`
        line-height: 38px;
      `}
    `}

  ${({ type, $hasSpinner }) =>
    type === 'number' &&
    css`
      ${$hasSpinner &&
      css`
        padding-right: 42px;
      `}
      &[type='number'] {
        -webkit-appearance: textfield;
        -moz-appearance: textfield;
        appearance: textfield;
      }

      &[type='number']::-webkit-inner-spin-button,
      &[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none;
      }
    `}

  ${({ $readOnly }) =>
    $readOnly &&
    css`
      caret-color: transparent;
    `}

  ${({ $strikethrough }) =>
    $strikethrough &&
    css`
      &:not(:focus) {
        text-decoration: line-through;
      }
    `}
`

export const SpinnerContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  padding: var(--space-50);
`

export const SpinnerItem = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  color: ${getTextColor};
  background-color: var(--bg-secondary);
  border: none;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`

export const Prepend = styled.div<PrependProps>`
  color: ${getTextColor};
  font-size: var(--font-size-300);
  position: absolute;
  left: var(--space-400);
  top: 1px;
  bottom: 1px;
  user-select: none;
  visibility: hidden;
  line-height: 1.5;
  letter-spacing: var(--font-letter-spacing-normal);
  color: var(--text-secondary);

  /* NOTE: Trim left side of prepend when its width is bigger than 50% of input */
  direction: rtl;
  max-width: 50%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  ${({ $size, $hasLabel }) =>
    $size === 'large' &&
    css`
      line-height: 54px;

      ${$hasLabel &&
      css`
        padding-top: var(--space-400);
        line-height: 38px;
      `}
    `}

  ${({ $size, $hasLabel }) =>
    $size === 'medium' &&
    css`
      line-height: 46px;

      ${$hasLabel &&
      css`
        padding-top: var(--space-350);
        line-height: 32px;
      `}
    `}

  ${({ $size }) =>
    $size === 'small' &&
    css`
      line-height: 38px;
    `}

  ${({ $visible }) =>
    $visible &&
    css`
      visibility: visible;
    `}
`

export const Append = styled(Prepend)<AppendProps>`
  right: var(--space-400);
  left: auto;
  direction: ltr;

  ${({ $largeSpace }) =>
    $largeSpace &&
    css`
      right: var(--space-900);
    `}
`

const getIconColor = ({ $hasValue }: IconContainerProps) => {
  if ($hasValue) {
    return getAutomatedStyleProp('colorOnBackground', 'var(--text-primary)')
  }

  return getAutomatedStyleProp('paleTextColor', 'var(--text-primary)')
}

export const IconContainer = styled.div<IconContainerProps>`
  position: absolute;
  padding-left: var(--space-300);
  color: ${getIconColor};

  ${({ $validElement }) =>
    $validElement &&
    css`
      padding: var(--space-100);
      cursor: pointer;
      color: var(--text-secondary);

      &:hover {
        color: var(--text-primary);
      }
    `}
`

export const ActionIconContainer = styled.div<ActionIconContainerProps>`
  position: absolute;
  right: 0;
  margin-right: var(--space-200);
  padding: var(--space-200);
  color: ${({ $iconColor }) => $iconColor || 'var(--text-secondary)'};

  ${({ $hoverable }) =>
    $hoverable &&
    css`
      cursor: pointer;

      &:hover {
        color: var(--text-primary);
      }
    `}

  ${({ $size }) =>
    $size === 'small' &&
    css`
      margin-right: var(--space-100);
    `}
`

export const Suggestion = styled.div<Pick<SuggestionProps, '$withoutMargin'>>`
  margin-top: ${({ $withoutMargin }) => ($withoutMargin ? 'none' : 'var(--space-200)')};

  color: ${getAutomatedStyleProp('paleTextColor', 'var(--text-secondary)')};
  font-size: var(--font-size-300);
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-300);

  a {
    text-decoration-line: underline;
    text-decoration-skip-ink: none;
    text-underline-offset: var(--space-50);

    &:hover {
      color: var(--text-brand);
    }
  }
`

export const Error = styled.div<Pick<SuggestionProps, '$withoutMargin'>>`
  display: flex;
  padding: ${({ theme }) =>
    theme.mode === 'opacity' ? 'var(--space-100) var(--space-150)' : '0px'};
  margin-top: ${({ theme, $withoutMargin }) => {
    if ($withoutMargin) return '0px'
    if (theme.mode === 'opacity') return 'var(--space-100)'

    return 'var(--space-200)'
  }};

  font-size: var(--font-size-300);
  line-height: var(--font-line-height-300);
  color: ${getAutomatedStyleProp('inputErrorText', 'var(--text-critical)')};
  background: ${({ theme }) =>
    theme.mode === 'opacity' ? theme.automated?.styles?.inputError : 'transparent'};
  border-radius: ${getAutomatedStyleProp('inputCornerRadius', 'var(--radius-brand)')};
`

export const Info = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: var(--space-100);
  font-size: var(--font-size-200);
  line-height: var(--font-line-height-200);
  text-align: right;
  color: ${getAutomatedStyleProp('paleTextColor', 'var(--text-secondary)')};
`
